import React from 'react';
import { X, Calendar, Clock, User, Package, FileText } from 'lucide-react';
import type { HistorialRecepcion } from '../types/ordenes';

interface ModalHistorialRecepcionesProps {
  isOpen: boolean;
  onClose: () => void;
  historial: HistorialRecepcion[];
  nombreIngrediente: string;
  unidadMedida: string;
  cantidadSolicitada: number;
  cantidadRecibida: number;
}

const ModalHistorialRecepciones: React.FC<ModalHistorialRecepcionesProps> = ({
  isOpen,
  onClose,
  historial,
  nombreIngrediente,
  unidadMedida,
  cantidadSolicitada,
  cantidadRecibida,
}) => {
  const formatearFechaCompleta = (fecha: Date | string) => {
    return new Date(fecha).toLocaleDateString('es-CL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatearHoraCompleta = (fecha: Date | string) => {
    return new Date(fecha).toLocaleString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (!isOpen) return null;

  const hayHistorial = historial && historial.length > 0;
  const cantidadRecibidaSinHistorial =
    cantidadRecibida -
    (hayHistorial
      ? historial.reduce((sum, h) => sum + h.cantidadRecibida, 0)
      : 0);

  // Ordenar historial por fecha de recepción
  const historialOrdenado = hayHistorial
    ? [...historial].sort(
        (a, b) =>
          new Date(a.fechaRecepcion).getTime() -
          new Date(b.fechaRecepcion).getTime()
      )
    : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header del Modal */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
              <Package className="h-6 w-6 mr-2 text-green-600" />
              Historial de Recepciones
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {nombreIngrediente}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Contenido del Modal */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
          {/* Resumen del Pedido */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Resumen del Pedido
            </h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="text-gray-500 dark:text-gray-400">
                  Solicitado
                </div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {cantidadSolicitada} {unidadMedida}
                </div>
              </div>
              <div className="text-center">
                <div className="text-gray-500 dark:text-gray-400">
                  Recibido Total
                </div>
                <div className="text-lg font-bold text-green-600 dark:text-green-400">
                  {cantidadRecibida} {unidadMedida}
                </div>
              </div>
              <div className="text-center">
                <div className="text-gray-500 dark:text-gray-400">
                  Pendiente
                </div>
                <div
                  className={`text-lg font-bold ${
                    cantidadSolicitada - cantidadRecibida > 0
                      ? 'text-orange-600 dark:text-orange-400'
                      : 'text-green-600 dark:text-green-400'
                  }`}
                >
                  {cantidadSolicitada - cantidadRecibida} {unidadMedida}
                </div>
              </div>
            </div>

            {/* Barra de progreso */}
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                <span>Progreso de Recepción</span>
                <span>
                  {Math.round((cantidadRecibida / cantidadSolicitada) * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
                <div
                  className="bg-green-500 h-3 rounded-full transition-all duration-300"
                  style={{
                    width: `${(cantidadRecibida / cantidadSolicitada) * 100}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Timeline de Recepciones */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
              <Calendar className="h-4 w-4 mr-2" />
              Timeline de Recepciones
            </h3>

            {!hayHistorial && cantidadRecibida === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No hay recepciones registradas para este producto</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Recepción anterior (si existe) */}
                {cantidadRecibidaSinHistorial > 0 && (
                  <div className="relative pl-8">
                    <div className="absolute left-0 top-2 w-6 h-6 bg-orange-100 dark:bg-orange-900 border-3 border-orange-500 rounded-full flex items-center justify-center">
                      <Package className="h-3 w-3 text-orange-600 dark:text-orange-400" />
                    </div>
                    {hayHistorial && (
                      <div className="absolute left-3 top-8 bottom-0 w-px bg-gray-200 dark:bg-gray-600" />
                    )}

                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-700">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-orange-800 dark:text-orange-200">
                          Recepción Anterior al Historial
                        </h4>
                        <span className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                          +{cantidadRecibidaSinHistorial} {unidadMedida}
                        </span>
                      </div>
                      <p className="text-sm text-orange-700 dark:text-orange-300">
                        Esta cantidad fue registrada antes de la implementación
                        del sistema de historial detallado.
                      </p>
                    </div>
                  </div>
                )}

                {/* Recepciones con historial detallado */}
                {historialOrdenado.map((recepcion, index) => (
                  <div key={recepcion.id} className="relative pl-8">
                    <div className="absolute left-0 top-2 w-6 h-6 bg-green-100 dark:bg-green-900 border-3 border-green-500 rounded-full flex items-center justify-center">
                      <span className="text-green-700 dark:text-green-300 text-xs font-bold">
                        {cantidadRecibidaSinHistorial > 0
                          ? index + 2
                          : index + 1}
                      </span>
                    </div>
                    {index < historialOrdenado.length - 1 && (
                      <div className="absolute left-3 top-8 bottom-0 w-px bg-gray-200 dark:bg-gray-600" />
                    )}

                    <div className="!bg-gray-50 dark:!bg-gray-800 rounded-lg p-4 border border-green-200 dark:border-green-700 shadow-sm dark:shadow-gray-900/20">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-green-800 dark:text-green-200">
                          {cantidadRecibidaSinHistorial > 0
                            ? index + 2
                            : index + 1}
                          ª Recepción
                        </h4>
                        <span className="text-lg font-bold text-green-600 dark:text-green-400">
                          +{recepcion.cantidadRecibida} {unidadMedida}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                          <Calendar className="h-4 w-4 mr-2" />
                          <div>
                            <div className="font-medium">
                              Fecha de Recepción
                            </div>
                            <div>
                              {formatearFechaCompleta(recepcion.fechaRecepcion)}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                          <User className="h-4 w-4 mr-2" />
                          <div>
                            <div className="font-medium">Recibido por</div>
                            <div>{recepcion.usuario.nombre}</div>
                          </div>
                        </div>
                      </div>

                      {recepcion.observaciones && (
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                          <div className="flex items-start">
                            <FileText className="h-4 w-4 mr-2 text-gray-500 mt-0.5" />
                            <div>
                              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Observaciones
                              </div>
                              <div className="text-sm text-gray-700 dark:text-gray-300">
                                "{recepcion.observaciones}"
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-3 flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <Clock className="h-3 w-3 mr-1" />
                        <span>
                          Registrado el{' '}
                          {formatearHoraCompleta(recepcion.fechaCreacion)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer del Modal */}
        <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalHistorialRecepciones;
